import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import Logo from "./Logo";

const NAV_ITEMS = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/find-a-job", label: "Find a Job" },
  { to: "/find-a-candidate", label: "Find a Candidate" },
  { to: "/help", label: "Help" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-cream/85 backdrop-blur-xl border-b border-ink-100"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
        <div className="flex items-center justify-between h-20 md:h-24">
          <Link
            to="/"
            data-testid="nav-logo"
            className="text-ink hover:text-stone-dark transition-colors"
          >
            <Logo size="md" />
          </Link>

          <nav className="hidden lg:flex items-center gap-9">
            {NAV_ITEMS.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                data-testid={`nav-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `text-sm font-body font-medium tracking-wide transition-colors relative ${
                    isActive ? "text-ink" : "text-stone-dark hover:text-ink"
                  } ${isActive ? "hand-underline active" : ""}`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:block">
            <Link
              to="/contact"
              data-testid="nav-cta-contact"
              className="inline-flex items-center gap-2 bg-ink text-mint hover:bg-stone-dark px-6 py-3 rounded-full font-body font-medium text-sm transition-transform hover:-translate-y-0.5"
            >
              Contact Us
              <span aria-hidden>→</span>
            </Link>
          </div>

          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen(!open)}
            className="lg:hidden p-2 text-ink"
            aria-label="Toggle menu"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {open && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden bg-cream border-t border-ink-100 animate-fade-in"
        >
          <nav className="max-w-7xl mx-auto px-6 py-8 flex flex-col gap-6">
            {NAV_ITEMS.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                data-testid={`mobile-nav-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `font-headings text-3xl ${isActive ? "text-stone-dark" : "text-ink"}`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <Link
              to="/contact"
              data-testid="mobile-nav-cta-contact"
              className="mt-4 inline-flex items-center justify-center gap-2 bg-ink text-mint px-6 py-4 rounded-full font-body font-medium"
            >
              Contact Us →
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}
